import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';

interface CommunityEvent {
  id: number;
  title: string;
  description: string;
  date: string;
  time: string;
  location: string;
  maxParticipants: number;
  currentParticipants: number;
  category: string;
  organizer: string;
  image: string;
  tags: string[];
  interestedUsers: string[];
  comments: Comment[];
}

interface Comment {
  id: number;
  user: string;
  content: string;
  timestamp: string;
}

const NomadPage: React.FC = () => {
  const { translations } = useLanguage();
  const [selectedEvent, setSelectedEvent] = useState<CommunityEvent | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [newComment, setNewComment] = useState('');

  const communityEvents: CommunityEvent[] = [
    {
      id: 1,
      title: "花蓮數位游牧聚會",
      description: "每月一次的數位游牧者聚會，分享工作經驗、交流技術心得，認識志同道合的朋友。提供免費咖啡和小點心。",
      date: "2024-01-20",
      time: "19:00-21:00",
      location: "花蓮數位工作坊",
      maxParticipants: 30,
      currentParticipants: 18,
      category: "工作聚會",
      organizer: "花蓮數位游牧社群",
      image: "https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=400&h=300&fit=crop",
      tags: ["networking", "coffee", "分享"],
      interestedUsers: ["張小明", "李小華", "王小美"],
      comments: [
        {
          id: 1,
          user: "張小明",
          content: "很期待這次的聚會！希望能認識更多在花蓮工作的數位游牧者。",
          timestamp: "2024-01-15 14:30"
        },
        {
          id: 2,
          user: "李小華",
          content: "上次聚會收穫很多，這次也會參加！",
          timestamp: "2024-01-15 16:45"
        }
      ]
    },
    {
      id: 2,
      title: "語言交換之夜",
      description: "中英文語言交換活動，提升語言能力同時認識國際朋友。適合想要練習外語或幫助外國朋友學習中文的朋友。",
      date: "2024-01-22",
      time: "18:30-20:30",
      location: "海景工作空間",
      maxParticipants: 20,
      currentParticipants: 12,
      category: "語言交換",
      organizer: "國際語言交流社",
      image: "https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=400&h=300&fit=crop",
      tags: ["語言", "國際", "交流"],
      interestedUsers: ["陳小強", "林小芳"],
      comments: [
        {
          id: 3,
          user: "陳小強",
          content: "想練習英文口說，這個活動很棒！",
          timestamp: "2024-01-16 10:20"
        }
      ]
    },
    {
      id: 3,
      title: "週末健行團",
      description: "週末一起到太魯閣健行，欣賞美麗的自然風景，同時認識新朋友。適合喜歡戶外活動的數位游牧者。",
      date: "2024-01-27",
      time: "08:00-16:00",
      location: "太魯閣國家公園",
      maxParticipants: 15,
      currentParticipants: 8,
      category: "戶外活動",
      organizer: "花蓮戶外俱樂部",
      image: "https://images.unsplash.com/photo-1551632811-561732d1e306?w=400&h=300&fit=crop",
      tags: ["健行", "戶外", "自然"],
      interestedUsers: ["王小美", "張小明", "李小華"],
      comments: [
        {
          id: 4,
          user: "王小美",
          content: "太魯閣的風景超美，很期待這次健行！",
          timestamp: "2024-01-16 15:30"
        }
      ]
    },
    {
      id: 4,
      title: "技術分享會",
      description: "分享最新的技術趨勢和開發經驗，包括前端、後端、AI等領域。適合對技術感興趣的開發者參加。",
      date: "2024-01-25",
      time: "19:00-21:00",
      location: "文創園區工作坊",
      maxParticipants: 25,
      currentParticipants: 15,
      category: "技術分享",
      organizer: "花蓮開發者社群",
      image: "https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=400&h=300&fit=crop",
      tags: ["技術", "開發", "AI"],
      interestedUsers: ["陳小強", "林小芳", "張小明"],
      comments: [
        {
          id: 5,
          user: "陳小強",
          content: "想聽聽AI相關的分享，現在這個領域發展很快。",
          timestamp: "2024-01-17 09:15"
        }
      ]
    },
    {
      id: 5,
      title: "攝影工作坊",
      description: "學習攝影技巧，捕捉花蓮的美麗風景。專業攝影師指導，適合想要提升攝影技能的愛好者。",
      date: "2024-01-28",
      time: "14:00-17:00",
      location: "七星潭",
      maxParticipants: 12,
      currentParticipants: 6,
      category: "攝影",
      organizer: "花蓮攝影協會",
      image: "https://images.unsplash.com/photo-1515187029135-18ee286d815b?w=400&h=300&fit=crop",
      tags: ["攝影", "風景", "技巧"],
      interestedUsers: ["李小華", "王小美"],
      comments: [
        {
          id: 6,
          user: "李小華",
          content: "七星潭的日落很美，很適合練習攝影！",
          timestamp: "2024-01-17 16:20"
        }
      ]
    }
  ];

  const toggleInterest = (eventId: number) => {
    // 這裡可以添加實際的API調用
    console.log(`Toggle interest for event ${eventId}`);
  };

  const addComment = (eventId: number) => {
    if (newComment.trim()) {
      // 這裡可以添加實際的API調用
      console.log(`Add comment to event ${eventId}: ${newComment}`);
      setNewComment('');
    }
  };

  const openEventModal = (event: CommunityEvent) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8 text-center">
          {translations.nomad.title}
        </h1>

        {/* Community Stats */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-hualien-blue">150+</div>
              <div className="text-gray-600">社群成員</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-hualien-green">25+</div>
              <div className="text-gray-600">每月活動</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-hualien-blue">5</div>
              <div className="text-gray-600">活躍群組</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-hualien-green">98%</div>
              <div className="text-gray-600">滿意度</div>
            </div>
          </div>
        </div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {communityEvents.map((event) => (
            <div key={event.id} className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300">
              <div className="relative h-48">
                <img
                  src={event.image}
                  alt={event.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4 bg-white px-2 py-1 rounded-full text-xs font-semibold text-hualien-blue">
                  {event.category}
                </div>
                <div className="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                  {event.currentParticipants}/{event.maxParticipants}
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{event.title}</h3>
                <p className="text-gray-600 mb-4 line-clamp-3">{event.description}</p>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-2">📅</span>
                    <span>{event.date} {event.time}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-2">📍</span>
                    <span>{event.location}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-2">👤</span>
                    <span>{event.organizer}</span>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {event.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-hualien-light-blue text-hualien-blue text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button
                    onClick={() => toggleInterest(event.id)}
                    className="flex-1 bg-hualien-blue text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  >
                    {translations.nomad.interested}
                  </button>
                  <button
                    onClick={() => openEventModal(event)}
                    className="flex-1 bg-white border border-hualien-blue text-hualien-blue py-2 px-4 rounded-lg font-medium hover:bg-hualien-light-blue transition-colors"
                  >
                    查看詳情
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Event Modal */}
        {showEventModal && selectedEvent && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold">{selectedEvent.title}</h3>
                <button
                  onClick={() => setShowEventModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <p className="text-gray-600">{selectedEvent.description}</p>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">日期時間：</span>
                    <span>{selectedEvent.date} {selectedEvent.time}</span>
                  </div>
                  <div>
                    <span className="font-medium">地點：</span>
                    <span>{selectedEvent.location}</span>
                  </div>
                  <div>
                    <span className="font-medium">主辦方：</span>
                    <span>{selectedEvent.organizer}</span>
                  </div>
                  <div>
                    <span className="font-medium">參與人數：</span>
                    <span>{selectedEvent.currentParticipants}/{selectedEvent.maxParticipants}</span>
                  </div>
                </div>

                {/* Comments Section */}
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-3">{translations.nomad.comment}</h4>
                  <div className="space-y-3 mb-4">
                    {selectedEvent.comments.map((comment) => (
                      <div key={comment.id} className="bg-gray-50 p-3 rounded-lg">
                        <div className="flex justify-between items-start mb-1">
                          <span className="font-medium text-sm">{comment.user}</span>
                          <span className="text-xs text-gray-500">{comment.timestamp}</span>
                        </div>
                        <p className="text-sm text-gray-700">{comment.content}</p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="寫下你的留言..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hualien-blue focus:border-transparent"
                    />
                    <button
                      onClick={() => addComment(selectedEvent.id)}
                      className="bg-hualien-blue text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                      發送
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Community Guidelines */}
        <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold mb-4">🤝 社群守則</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
            <div>
              <h3 className="font-medium mb-2">參與原則</h3>
              <ul className="space-y-1">
                <li>• 尊重他人，保持友善的交流氛圍</li>
                <li>• 分享有價值的內容和經驗</li>
                <li>• 遵守活動規則和時間安排</li>
              </ul>
            </div>
            <div>
              <h3 className="font-medium mb-2">活動須知</h3>
              <ul className="space-y-1">
                <li>• 請提前報名，準時參加活動</li>
                <li>• 如需取消請提前通知主辦方</li>
                <li>• 歡迎拍照分享，但請尊重他人隱私</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NomadPage; 